import * as React from 'react'
import { useMemo } from 'react'
import { StackScreenProps } from '@react-navigation/stack'
import type { AdminNavigatorParams } from '../../navigation/types'
import { useTranslation } from 'react-i18next'
import { ScreenWrapper } from '../../@ui/containers/grid/screen.wrapper'
import Column from '../../@ui/containers/grid/column'
import Row from '../../@ui/containers/grid/row'
import { Text, Card, List, Icon } from 'react-native-paper'
import { View } from 'react-native'
import { addMonths, format, startOfMonth } from 'date-fns'
import { useAdminStatsForRangeQuery } from '../../generated/graphql'
import { useTheme } from '../../hooks/theme.hook'
import { useIsMobileDevice } from '../../hooks/is-mobile.hook'
import { AdminStatMetricCardSkeleton } from '../../@ui'
import { formatCurrencyAmount } from '../node-admin/payment/payment.utils'

const STAT_CARD_OUTER = { width: '100%' as const }

type AdminStatMetricCardProps = {
  icon: string
  title: string
  /** Pre-formatted this-period value */
  current?: string
  /** Pre-formatted previous-period value */
  previous?: string
  /** e.g. February 2026 — shown under the current-period figure */
  currentMonthName: string
  /** e.g. January 2026 — shown under the previous-period figure */
  previousMonthName: string
  loading: boolean
}

function AdminStatMetricCard({
  icon,
  title,
  current,
  previous,
  currentMonthName,
  previousMonthName,
  loading,
}: AdminStatMetricCardProps) {
  const theme = useTheme()
  const isMobile = useIsMobileDevice()

  if (loading) {
    return <AdminStatMetricCardSkeleton />
  }

  return (
    <Card style={STAT_CARD_OUTER}>
      <Card.Content style={{ gap: 8 }}>
        <Row alignItems="center" columnGap={2}>
          <Icon source={icon} size={28} color={theme.colors.primary} />
          <Text variant="titleMedium" style={{ flex: 1 }}>
            {title}
          </Text>
        </Row>

        <Row alignItems="flex-end" columnGap={12}>
          <Column
            gap={1}
            style={{ flex: 1, minWidth: 0 }}
            justifyContent="flex-end"
          >
            <Text
              variant={isMobile ? 'headlineSmall' : 'headlineLarge'}
              style={{ fontWeight: '600' }}
            >
              {current ?? '—'}
            </Text>
            <Text
              variant="bodySmall"
              style={{ opacity: 0.65 }}
              numberOfLines={2}
            >
              {currentMonthName}
            </Text>
          </Column>
          <Column gap={1} style={{ maxWidth: '42%', flexShrink: 0 }}>
            <Text
              variant={isMobile ? 'titleSmall' : 'titleMedium'}
              numberOfLines={2}
            >
              {previous ?? '—'}
            </Text>
            <Text
              variant="bodySmall"
              style={{ opacity: 0.65 }}
              numberOfLines={2}
            >
              {previousMonthName}
            </Text>
          </Column>
        </Row>
      </Card.Content>
    </Card>
  )
}

export function AdminHomeScreen({
  navigation,
}: StackScreenProps<AdminNavigatorParams, 'AdminHome'>) {
  const { t } = useTranslation('admin')
  const { t: tTiers } = useTranslation('tiers')
  const isMobile = useIsMobileDevice()

  const calendarMonth = format(new Date(), 'yyyy-MM')
  const ranges = useMemo(() => {
    const ref = new Date()
    const thisStart = startOfMonth(ref)
    const thisEndExclusive = addMonths(thisStart, 1)
    const prevStart = addMonths(thisStart, -1)
    return {
      thisFrom: thisStart.toISOString(),
      thisTo: thisEndExclusive.toISOString(),
      prevFrom: prevStart.toISOString(),
      prevTo: thisStart.toISOString(),
      currentMonthName: format(thisStart, 'MMMM yyyy'),
      previousMonthName: format(prevStart, 'MMMM yyyy'),
    }
  }, [calendarMonth])

  const currentQuery = useAdminStatsForRangeQuery({
    variables: { from: ranges.thisFrom, to: ranges.thisTo },
    fetchPolicy: 'cache-and-network',
  })
  const prevQuery = useAdminStatsForRangeQuery({
    variables: { from: ranges.prevFrom, to: ranges.prevTo },
    fetchPolicy: 'cache-and-network',
  })

  const statsError = currentQuery.error ?? prevQuery.error
  const cur = currentQuery.data?.adminStatsForRange
  const prev = prevQuery.data?.adminStatsForRange

  const statsLoading =
    !statsError &&
    (currentQuery.data == null || prevQuery.data == null) &&
    (currentQuery.loading || prevQuery.loading)

  const fmtInt = (n: number | undefined) =>
    n === undefined ? undefined : String(n)
  const fmtBilling = (n: number | undefined) =>
    n === undefined ? undefined : formatCurrencyAmount(tTiers, n)

  return (
    <ScreenWrapper style={{ padding: 16 }} withScrollView>
      <Column gap={4}>
        <View>
          <Text variant="titleMedium" style={{ marginBottom: 8 }}>
            {t('stats-overview-title')}
          </Text>
          {statsError ? (
            <Text variant="bodyMedium" style={{ color: '#b00020' }}>
              {t('stats-load-error')}
            </Text>
          ) : null}
          <Row gap={3} wrap style={{ alignSelf: 'stretch' }}>
            <View style={{ width: isMobile ? '100%' : '31.5%' }}>
              <AdminStatMetricCard
                icon="account-group-outline"
                title={t('stat-active-users')}
                current={fmtInt(cur?.activeUsers)}
                previous={fmtInt(prev?.activeUsers)}
                currentMonthName={ranges.currentMonthName}
                previousMonthName={ranges.previousMonthName}
                loading={statsLoading}
              />
            </View>
            <View style={{ width: isMobile ? '100%' : '31.5%' }}>
              <AdminStatMetricCard
                icon="cube-outline"
                title={t('stat-new-nodes')}
                current={fmtInt(cur?.newNodes)}
                previous={fmtInt(prev?.newNodes)}
                currentMonthName={ranges.currentMonthName}
                previousMonthName={ranges.previousMonthName}
                loading={statsLoading}
              />
            </View>
            <View style={{ width: isMobile ? '100%' : '31.5%' }}>
              <AdminStatMetricCard
                icon="cash-multiple"
                title={t('stat-billing-total')}
                current={fmtBilling(cur?.billingTotal)}
                previous={fmtBilling(prev?.billingTotal)}
                currentMonthName={ranges.currentMonthName}
                previousMonthName={ranges.previousMonthName}
                loading={statsLoading}
              />
            </View>
          </Row>
        </View>

        <View>
          <Text variant="titleMedium" style={{ marginBottom: 8 }}>
            {t('menus-title')}
          </Text>
          <Card>
            <List.Item
              title={t('coupon-management')}
              description={t('coupon-management-desc')}
              left={(props) => (
                <List.Icon {...props} icon="ticket-percent-outline" />
              )}
              onPress={() => navigation.navigate('CouponManagement')}
            />
          </Card>
        </View>
      </Column>
    </ScreenWrapper>
  )
}
